// src/components/Groups/CommonProjectList.tsx
import React from 'react';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import { Badges, H5, Image, LI, P, UL, Btn } from '../../../../AbstractElements';
import { dynamicImage } from '../../../../Utils';
import { Edit, Archive, CheckCircle, Users } from 'react-feather';
import { useArchiveGroup } from '../../../../hooks/useArchiveGroup';
import { useDispatch } from 'react-redux';
import { setGroupDetails } from '../../../../ReduxToolkit/GroupReducers/GroupReducer';
import { useNavigate } from 'react-router-dom';
import Authorized from '../../../../CommonElements/Authorisation/Authorise';

// Type adjusted for your group data
interface GroupItemProps {
  item: any;
  refetchGroups: () => void; // Add refetchGroups prop
}

export default function CommonProjectList({ item, refetchGroups }: GroupItemProps) {
  const { handleArchiveGroup, loading: archiving } = useArchiveGroup();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const redirectEdit = () => {
    dispatch(setGroupDetails(item));
    navigate(`${process.env.PUBLIC_URL}/group/edit`);
  };

  const handleArchiveToggle = async () => {
    await handleArchiveGroup(item?._id, !item?.isArchived);
    refetchGroups(); // Refetch groups after archiving/unarchiving
  };

  // Badge based on archived flag
  const badge = item?.isArchived ? 'Archived' : 'Active';

  // Fallbacks if participants < 3
  const [p1, p2, p3] = item?.participants || [];

  // navigate to participants screen and pass participants in location.state
  const viewParticipants = () => {
    navigate(`${process.env.PUBLIC_URL}/group/participants`, {
      state: {
        participants: item?.participants || [],
        groupId: item?._id,
        groupTitle: item?.title
      }
    });
  };
  return (
    <Col xxl={4} md={6}>
      <div className={`project-box font-dark`} style={{borderColor:"#FF3500"}}>
        <Badges color="#FF3500">{badge}</Badges>

        {/* Group title */}
        <H5 className={`f-w-500 mb-2`} style={{color:"#FF3500"}}>
          {item?.title}
        </H5>

        <Authorized allowed={['admin', 'group leader']}>
          <div className="text-end mb-2">
            <button className="btn btn-sm btn-primary me-2" onClick={redirectEdit} type="button" title="Edit Group">
              <Edit size={14} />
            </button>
            <button
              className={`btn btn-sm`}
              style={{backgroundColor:"#FF3500",color:"#FFFFFF"}}
              onClick={handleArchiveToggle}
              disabled={archiving}
              type="button"
              title={item?.isArchived ? 'Unarchive' : 'Archive'}
            >
              {archiving ? 'Processing...' : item?.isArchived ? <CheckCircle size={14} /> : <Archive size={14} />}
            </button>
            {/* VIEW PARTICIPANTS BUTTON */}

          </div>
        </Authorized>

        {/* Created by + Book */}
        <div className="d-flex mb-2 align-items-center">
          {/* <Image
            className="img-20 me-1 rounded-circle"
            src={dynamicImage(`user/${item?.participants?.[0]?.image || '1.jpg'}`)}
            alt=""
          /> */}
          <P className="font-light">{item?.bookName}</P>
        </div>

        {/* Description */}
        <P>{item?.description}</P>

        {/* Group details */}
        <Row className="details">
          <Col xs={6}>
            <span>{'Created By'} </span>
          </Col>
          <Col xs={6} style={{color:"#FF3500"}}>
            {item?.createdBy?.firstname} {item?.createdBy?.lastname}
          </Col>
          <Col xs={6}>
            <span>{'Start Date'} </span>
          </Col>
          <Col xs={6} style={{color:"#FF3500"}}>
            {item?.date ? new Date(item?.date).toLocaleDateString() : '—'}
          </Col>
          <Col xs={6}>
            <span>{'Participants'} </span>
          </Col>
          <Col xs={6} className="d-flex align-items-center justify-content-end">
            {/* Left: label + pill count (keeps same look for Active / Archived) */}
            <div className="me-3 d-flex align-items-center gap-2">
              <small className="text-muted d-block">Participants</small>

              {/* count pill — subtle background, colored text to match badge */}
              <span
                className={`rounded-pill px-2 py-1 fw-semibold`}
                style={{
                  fontSize: 12,
                  lineHeight: 1,
                  // gentle translucent background that works in light/dark
                  background: badge === 'Active' ? 'rgba(25, 135, 84, 0.08)' : 'rgba(220, 53, 69, 0.08)',
                  color: '#FF3500',
                  border: `1px solid ${badge === 'Active' ? 'rgba(25,135,84,0.12)' : 'rgba(220,53,69,0.12)'}`
                }}
              >
                {item?.participants?.length ?? 0}
              </span>
            </div>

            {/* Right: View participants button */}
            <div>
              <Authorized allowed={['admin', 'group leader']}>
                <button
                  id={`view-participants-btn-${item?._id}`}
                  className="btn btn-sm btn-outline-primary d-flex align-items-center"
                  onClick={viewParticipants}
                  type="button"
                  aria-label="View all participants"
                  title="View all participants"
                >
                  <Users size={14} className="me-2" />
                  {/* hide text on very small screens to save space */}
                  <span className="d-none d-sm-inline">View Participants</span>
                </button>

              {/* tooltip (uses unique id so multiple cards are ok) */}
              <UncontrolledTooltip placement="top" target={`view-participants-btn-${item?._id}`}>
                View all participants in this group
              </UncontrolledTooltip>

                            </Authorized>

            </div>
          </Col>
        </Row>

        {/* Participants avatars */}
        <div className="customers mt-3">
          <UL className="simple-list flex-row align-items-center">
            {p1 && (
              <LI className="d-inline-block">
                {/* <Image
                  className="img-30 rounded-circle"
                  src={dynamicImage(`user/${p1.image || '2.jpg'}`)}
                  alt=""
                /> */}
              </LI>
            )}
            {p2 && (
              <LI className="d-inline-block">
                {/* <Image
                  className="img-30 rounded-circle"
                  src={dynamicImage(`user/${p2.image || '3.jpg'}`)}
                  alt=""
                /> */}
              </LI>
            )}
            {p3 && (
              <LI className="d-inline-block">
                {/* <Image
                  className="img-30 rounded-circle"
                  src={dynamicImage(`user/${p3.image || '4.jpg'}`)}
                  alt=""
                /> */}
              </LI>
            )}
            {item?.participants?.length > 3 && (
              <LI className="d-inline-block ms-2">
                <P className="f-12">{`+${item?.participants.length - 3} More`}</P>
              </LI>
            )}
          </UL>
        </div>
      </div>
    </Col>
  );
}
